// Design tokens for the redesigned homepage (Figma: Marketing-Website-GA).
// Colors and fonts are lifted from the Figma "Use Cases" design-context styles.

import { BODY_FONT, HEADING_FONT } from '@/lib/constants'
import { hexToRgb } from '@/lib/utils'

// Neutral 1–9 form a single light → dark ramp (decreasing luminance).
export const COLORS = {
  /** Green 1 — primary mint accent (CTAs, links). */
  green1: '#BAFFD5',
  /** Green 2 — saturated card-accent green. */
  green2: '#67CF91',
  /** Green 3 — deep green (text on light cards, dark panels). */
  green3: '#07361C',
  /** Neutral 1 — near-white body copy. */
  neutral1: '#FEFEFE',
  /** Neutral 2 — warm cream (display headings, legal light text). */
  neutral2: '#F8F6F2',
  /** Neutral 3 — card cream background, legal table header (light). */
  neutral3: '#E1DBD2',
  /** Neutral 4 — light warm grey (legal page background, table borders). */
  neutral4: '#D1CBC1',
  /** Neutral 5 — muted warm grey. */
  neutral5: '#C1BAAF',
  /** Neutral 6 — mid warm grey (legal page background). */
  neutral6: '#595247',
  /** Neutral 7 — dark warm brown (legal table headers, borders). */
  neutral7: '#4A4338',
  /** Neutral 8 — dark warm near-black (page background). */
  neutral8: '#1F1D17',
  /** Neutral 9 — warm near-black (dark section backgrounds, gradient base). */
  neutral9: '#181512',
} as const

// Token colors as comma-separated RGB channels, for use inside `rgba(...)`
// when a token color is needed at partial opacity (e.g. `rgba(${NEUTRAL9_RGB}, 0.5)`).
export const GREEN1_RGB = hexToRgb(COLORS.green1)
export const NEUTRAL1_RGB = hexToRgb(COLORS.neutral1)
export const NEUTRAL2_RGB = hexToRgb(COLORS.neutral2)
export const NEUTRAL8_RGB = hexToRgb(COLORS.neutral8)
export const NEUTRAL9_RGB = hexToRgb(COLORS.neutral9)

export const FONTS = {
  heading: HEADING_FONT, // 'Sentient', Georgia, serif
  body: BODY_FONT, // 'Inter', sans-serif
  // Eyebrow labels are IBM Plex Mono (self-hosted woff2 in /public/fonts,
  // declared in FontLoader). System mono stack remains as the fallback.
  mono: "'IBM Plex Mono', ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace",
} as const

// Shared eyebrow label style — the site's mono-uppercase idiom. Spread this and
// override only color/margin/positioning per instance:
//   <p style={{ ...EYEBROW, color: COLORS.muted }}>Section label</p>
export const EYEBROW: React.CSSProperties = {
  fontFamily: FONTS.mono,
  fontSize: 'var(--text-xs)',
  fontWeight: 600,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  margin: 0,
}

// Shared easing — matches the rest of the site (power-style ease-out).
export const EASE_OUT: [number, number, number, number] = [0.25, 1, 0.5, 1]

// Standard pill-button height (px). Used by CtaButton's outline/solid variants.
// 44px meets the minimum touch-target size for mobile accessibility.
export const BUTTON_HEIGHT = 44

// Horizontal gutter matching Figma's ~160px margin on a 1728px canvas (≈9.26%),
// clamped so it never crowds on small screens.
export const GUTTER = 'clamp(24px, 9.26vw, 160px)'

/** Inner section wrapper: full width, centered max-width, Figma-matched gutters. */
export const sectionInner: React.CSSProperties = {
  width: '100%',
  maxWidth: 1728,
  margin: '0 auto',
  paddingLeft: GUTTER,
  paddingRight: GUTTER,
  boxSizing: 'border-box',
}
